import { lazy } from 'react'
import { generatePath, Redirect, useLocation, useParams } from 'react-router-dom'
import { DashSwitch } from '@retailer-platform/dashboard/routing'
import { DomainRoute, DomainRedirect } from '../utils/domain/routingComponents'
import {
  CustomerOrderHistoryViewAccessControl,
  CustomerSearchViewAccessControl,
  CustomersAndOrdersViewAccessControl,
  OrderDetailViewAccessControl,
} from '../access-control/customersAndOrdersAdminAccess.configuration'
import { useDomainAccessControl } from '../utils/domain/accessControl'
import { useGetOrderByIdBasic } from '../utils/hooks/useGetOrderByIdBasic.hooks'
import { buildOrderUrl } from '../utils/domain/routing'
import { routes } from './routes'

import FastFindProvider from '../components/FastFindProvider'

const CustomersAndOrdersAdminPage = lazy(
  () =>
    import(
      /* webpackChunkName: "CustomersAndOrdersAdminPage" */
      '../pages/admin/customers-and-orders/CustomersAndOrdersAdminPage'
    )
)

// Redirects users with IPP search access to the new IPP search page, preserving any ?query=... param.
// Mounted on the legacy fast-find URL and the embedded cx-admin landing URL. Users without IPP search
// access continue to see the embedded iframe.
const IppSearchRedirect = () => {
  const { search } = useLocation()
  const hasAccess = useDomainAccessControl()

  if (!hasAccess(CustomerSearchViewAccessControl)) {
    return <CustomersAndOrdersAdminPage />
  }

  return (
    <Redirect
      to={{
        pathname: routes['app-admin-customers-and-orders-ipp-customer-search'],
        search,
      }}
    />
  )
}

// Redirects users with IPP order-history access from the embedded /customers/:customerId/orders URL
// to the new IPP order history page. Users without IPP access continue to see the embedded iframe.
const LegacyCustomerOrdersRedirect = () => {
  const { customerId } = useParams<{ customerId: string }>()
  const hasAccess = useDomainAccessControl()

  if (!hasAccess(CustomerOrderHistoryViewAccessControl)) {
    return <CustomersAndOrdersAdminPage />
  }

  return (
    <Redirect
      to={generatePath(routes['app-admin-customers-and-orders-ipp-customer-order-history'], {
        customerId,
      })}
    />
  )
}

// Redirects users with native order-details access from the bare legacy order URL
// (/dashboard/admin/orders/:orderId — no customer in the path) to the customer-scoped native IPP
// order-details page. The native route is customer-scoped, so the order's user id is resolved via
// an async lookup first. Users without access, a non-numeric id, or an order that can't be resolved
// fall through to the embedded legacy iframe (matching the other legacy redirects here).
export const LegacyBareOrderRedirect = () => {
  const { orderId } = useParams<{ orderId: string }>()
  const hasAccess = useDomainAccessControl()
  const hasOrderAccess = hasAccess(OrderDetailViewAccessControl)
  const numericOrderId = /^\d+$/.test(orderId ?? '') ? orderId : null
  // Skip the lookup entirely when the user lacks access or the id isn't a plain order id.
  const { data: order, loading } = useGetOrderByIdBasic(hasOrderAccess ? numericOrderId : null)

  if (!hasOrderAccess || !numericOrderId) {
    return <CustomersAndOrdersAdminPage />
  }

  if (loading) return null

  if (!order?.userId || !order?.id) {
    return <CustomersAndOrdersAdminPage />
  }

  return <Redirect to={buildOrderUrl(String(order.userId), String(order.id))} />
}

const CustomerOrderHistoryPage = lazy(
  () =>
    import(
      /* webpackChunkName: "CustomerOrderHistoryPage" */
      '../pages/admin/customers-and-orders/CustomerOrderHistoryPageWithLegacyFallback'
    )
)

const CustomerSearchLandingPage = lazy(
  () =>
    import(
      /* webpackChunkName: "CustomerSearchLandingPage" */
      '../pages/admin/customers-and-orders/CustomerSearchLandingPageWithLegacyFallback'
    )
)

const CustomerDetailPageWithLegacyFallback = lazy(
  () =>
    import(
      /* webpackChunkName: "CustomersAndOrdersAdminPage" */
      '../pages/admin/customers-and-orders/CustomerDetailPageWithLegacyFallback'
    )
)

const OrderDetailPageWithLegacyFallback = lazy(
  () =>
    import(
      /* webpackChunkName: "CustomersAndOrdersOrderDetailPage" */
      '../pages/admin/customers-and-orders/OrderDetailPageWithLegacyFallback'
    )
)

// Note that this is intentionally not a component. Do not wrap <DomainRoute> with anything else, as it will cause issues with the parent <Switch />
// More information here https://v5.reactrouter.com/web/api/Switch/children-node
export const customersAndOrdersAdminRouter = (
  <DomainRoute route="app-admin-customers-and-orders">
    <FastFindProvider>
      <DashSwitch>
        <DomainRedirect
          fromRoute="app-admin-customers-and-orders-dashboard"
          toRoute="app-admin-customers-and-orders-dashboard-legacy"
          exact
        />
        <DomainRoute
          exact
          route="app-admin-customers-and-orders-ipp-customer-order-history"
          component={CustomerOrderHistoryPage}
        />
        <DomainRoute
          exact
          route="app-admin-customers-and-orders-ipp-customer-search"
          component={CustomerSearchLandingPage}
        />
        <DomainRoute
          exact
          route="app-admin-customers-and-orders-ipp-customer-details"
          component={CustomerDetailPageWithLegacyFallback}
        />
        <DomainRoute
          exact
          route="app-admin-customers-and-orders-ipp-customer-order-details"
          component={OrderDetailPageWithLegacyFallback}
        />
        <DomainRoute
          exact
          route="app-admin-customers-and-orders-dashboard-legacy"
          component={IppSearchRedirect}
        />
        <DomainRoute
          exact
          route="app-admin-customers-and-orders-legacy-fast-find"
          component={IppSearchRedirect}
        />
        <DomainRoute
          exact
          route="app-admin-customers-and-orders-legacy-customer-orders"
          component={LegacyCustomerOrdersRedirect}
        />
        <DomainRoute
          exact
          route="app-admin-customers-and-orders-legacy-care-customer-orders"
          component={LegacyCustomerOrdersRedirect}
        />
        <DomainRoute
          exact
          route="app-admin-customers-and-orders-legacy-order-details"
          component={LegacyBareOrderRedirect}
        />
        <DomainRoute
          exact
          route="app-admin-customers-and-orders-legacy-orders-index"
          component={IppSearchRedirect}
        />
        <DomainRoute
          route="app-admin-customers-and-orders-dashboard-wildcard"
          component={CustomersAndOrdersAdminPage}
          accessControl={CustomersAndOrdersViewAccessControl}
        />
      </DashSwitch>
    </FastFindProvider>
  </DomainRoute>
)
